import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight,
  Globe,
  Shield,
  Code2,
  Database,
  Wifi,
  Briefcase,
} from "lucide-react";
import { servicesData } from "../../data/services";

const iconMap: Record<string, React.ElementType> = {
  development: Code2,
  "e-procurement": Briefcase,
  "security-solutions": Shield,
  "ict-systems-support": Wifi,
  consultancy: Database,
};

const badgeMap: Record<string, string> = {
  "security-solutions": "High demand",
  "e-procurement": "For institutions",
  development: "Modern builds",
  "ict-systems-support": "Reliable support",
  consultancy: "Strategy first",
};

const Services = () => {
  const location = useLocation();
  const showCTA = location.pathname !== "/services";

  return (
    <section
      id="services"
      className="relative overflow-hidden bg-gradient-to-br from-slate-200 via-white to-indigo-200 py-24"
    >
      {/* SEO Metadata */}
      <Helmet>
        <title>Our Services | Northstart Systems</title>
        <meta
          name="description"
          content="Explore Northstart Systems services including Web Development, E-Procurement, Security Installations, ICT Infrastructure, IT Support, and Tech Partnerships."
        />
        <link rel="canonical" href="https://northstartsystems.com/services" />
      </Helmet>

      {/* Soft Accent Background */}
      <div className="pointer-events-none absolute -top-32 left-1/4 h-96 w-96 rounded-full bg-blue-200/40 blur-3xl" />
      <div className="pointer-events-none absolute top-24 right-1/4 h-[30rem] w-[30rem] rounded-full bg-indigo-200/30 blur-3xl" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(99,102,241,0.10),transparent_50%),radial-gradient(circle_at_80%_10%,rgba(59,130,246,0.10),transparent_45%),radial-gradient(circle_at_50%_90%,rgba(14,165,233,0.08),transparent_55%)]" />

      <div className="relative mx-auto max-w-7xl px-4">
        {/* Header */}
        <div className="mx-auto mb-16 max-w-4xl text-center">
          <h2 className="mt-6 text-4xl font-extrabold tracking-tight text-slate-900 md:text-5xl">
            Our{" "}
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Services
            </span>
          </h2>

          <p className="mt-4 text-lg leading-relaxed text-slate-600 md:text-xl">
            Practical digital solutions that help organizations stay secure,
            efficient, and ready to scale.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {servicesData.map((service) => {
            const Icon = iconMap[service.id] || Globe;
            const badge = badgeMap[service.id] || "";

            return (
              <Link
                key={service.id}
                to={`/service/${service.id}`}
                className="group"
                aria-label={`Open ${service.title}`}
              >
                <div className="relative h-full rounded-3xl border border-slate-200 bg-white/80 p-7 shadow-sm backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
                  {/* top row */}
                  <div className="flex items-start justify-between gap-4">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100">
                      <Icon className="h-6 w-6" />
                    </span>

                    {badge && (
                      <span className="rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-bold text-indigo-700">
                        {badge}
                      </span>
                    )}
                  </div>

                  <h3 className="mt-5 text-xl font-extrabold text-slate-900">
                    {service.title}
                  </h3>

                  <p className="mt-3 text-sm leading-relaxed text-slate-600">
                    {service.shortDescription}
                  </p>

                  <div className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-indigo-700">
                    Explore solution
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </div>

                  {/* subtle hover ring */}
                  <div className="pointer-events-none absolute inset-0 rounded-3xl opacity-0 ring-1 ring-indigo-300/40 transition-opacity duration-300 group-hover:opacity-100" />
                </div>
              </Link>
            );
          })}
        </div>

        {/* CTA (best on home page) */}
        {showCTA && (
          <div className="mt-14 text-center">
            <Link
              to="/services"
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-600 to-blue-600 px-8 py-4 text-base font-extrabold text-white shadow-lg shadow-indigo-600/20 transition hover:from-indigo-500 hover:to-blue-500 hover:shadow-indigo-600/30"
            >
              <Globe className="h-5 w-5" />
              View All Solutions
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default Services;